// Public kiosk page (/kiosk?t=<token>&equnr=<id>) — the no-login malfunction-report funnel.
// Opened from the QR label on a machine by production operators. Deliberately self-contained:
// no auth, no app providers, no i18n context (bilingual static labels instead — the audience
// is the shop floor, not a configured portal user). Server side enforces token + rate limit.
import React, { useState, useEffect } from 'react';

const qs = new URLSearchParams(window.location.search);
const TOKEN = qs.get('t') || '';
const EQUNR = qs.get('equnr') || '';
// One idempotency key per PAGE LOAD, minted at module scope beside TOKEN/EQUNR. Unauthenticated,
// shared shop-floor phone, gloves, workshop wifi: without it a lost response makes the operator
// tap Send again and maintenance gets two identical breakdown reports — with the urgent flag, two
// escalations (audit finding 21). The backend reads `idem` (max 64) and falls back to a server
// UUID, so a cached page that sends nothing still works. A full reload stays undeduplicated:
// far rarer, and the alternative is storage this page deliberately does not use.
// randomUUID is missing on the older shop-floor browsers this page must still serve.
const IDEM = (() => {
  try { return crypto.randomUUID(); } catch { /* fall through */ }
  return `kiosk-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
})();
// SAP QMEL-QMTXT is CHAR40 and silently keeps the first 40 characters. Mirrors SAP_SHORT_TEXT_MAX
// in src/modules/plant-maintenance/constants.js — NOT imported: this is a public page that pulls
// in no module code (no i18n, no providers). Keep the two in step.
const SHORT_TEXT_MAX = 40;

// The backend speaks English only; every other string on this page is bilingual, so its errors
// are mapped here rather than printed raw (audit finding 21).
const ERR_RATE = 'Too many reports for this machine right now — please wait a few minutes and try again. / Zu viele Meldungen für diese Maschine — bitte einige Minuten warten und erneut versuchen.';
const ERR_GENERIC = 'The report could not be sent — please try again. / Die Meldung konnte nicht gesendet werden — bitte erneut versuchen.';

const styles = {
  page: { minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', fontFamily: 'system-ui, sans-serif', padding: 16, boxSizing: 'border-box' },
  card: { maxWidth: 480, margin: '24px auto', background: '#1e293b', borderRadius: 16, padding: 20 },
  h1: { fontSize: 20, margin: '0 0 4px' },
  sub: { color: '#94a3b8', fontSize: 14, margin: '0 0 16px' },
  label: { display: 'block', fontSize: 13, color: '#94a3b8', margin: '12px 0 4px' },
  input: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 10, padding: '12px 14px', fontSize: 16 },
  check: { display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0', fontSize: 15 },
  btn: { width: '100%', marginTop: 16, background: '#f59e0b', color: '#1c1917', border: 'none', borderRadius: 12, padding: '16px', fontSize: 17, fontWeight: 700, cursor: 'pointer' },
  ok: { color: '#6ee7b7', fontSize: 16, marginTop: 12 },
  err: { color: '#fda4af', fontSize: 14, marginTop: 12 },
  errDetail: { color: '#94a3b8', fontSize: 12, marginTop: 4 },
  counter: { color: '#64748b', fontSize: 12, textAlign: 'right', margin: '4px 0 0' },
};

export default function KioskPage() {
  const [equipment, setEquipment] = useState(null);
  const [text, setText] = useState('');
  const [reporter, setReporter] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);

  useEffect(() => {
    if (!TOKEN || !EQUNR) return;
    fetch(`/api/pm-kiosk/equipment?t=${encodeURIComponent(TOKEN)}&equnr=${encodeURIComponent(EQUNR)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((e) => setEquipment(e))
      .catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setErrorDetail(null);
    // A stalled connection on workshop wifi must not leave the button disabled forever — the
    // operator has no way back other than reloading, which loses the typed text.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch('/api/pm-kiosk/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ t: TOKEN, equnr: EQUNR, text, reporter, urgent, idem: IDEM }),
        signal: controller.signal,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body.error || `HTTP ${res.status}`);
        err.status = res.status; // 429 is the one case the operator can act on themselves
        throw err;
      }
      setDone(body.notificationId);
    } catch (e) {
      const aborted = e && e.name === 'AbortError';
      setError(e && e.status === 429 ? ERR_RATE : ERR_GENERIC);
      // The backend detail helps the Meister who gets called over; the browser's own abort text
      // ("signal is aborted…") helps nobody, so it is not shown.
      setErrorDetail(!aborted && e && e.message ? e.message : null);
    } finally {
      clearTimeout(timer);
      setBusy(false);
    }
  };

  if (!TOKEN) {
    return (
      <div style={styles.page}><div style={styles.card}>
        <h1 style={styles.h1}>Report a problem / Problem melden</h1>
        <p style={styles.sub}>This page only works via the QR code on the machine. / Diese Seite funktioniert nur über den QR-Code an der Maschine.</p>
      </div></div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.h1}>Report a problem / Problem melden</h1>
        <p style={styles.sub}>
          {equipment
            ? `${equipment.equipmentId} — ${equipment.description}`
            : (EQUNR ? `Equipment ${EQUNR}` : 'Equipment unknown / unbekannt')}
        </p>

        {done ? (
          <p style={styles.ok}>
            ✔ Reported — maintenance has been informed. / Gemeldet — die Instandhaltung ist informiert.
            <br />Report no. / Meldungsnr.: <b>{done.replace(/^0+/, '')}</b>
          </p>
        ) : (
          <>
            <label style={styles.label}>What is wrong? / Was ist kaputt?</label>
            <textarea style={{ ...styles.input, minHeight: 90 }} maxLength={SHORT_TEXT_MAX} value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. loud grinding noise / z. B. lautes Schleifgeräusch" />
            {/* The cap is shown while typing — SAP would otherwise keep the first 40 characters
                and drop the rest without telling anyone (audit finding 33). */}
            <p style={styles.counter}>{text.length}/{SHORT_TEXT_MAX}</p>
            <label style={styles.label}>Your name (optional) / Ihr Name (optional)</label>
            <input style={styles.input} maxLength={40} value={reporter} onChange={(e) => setReporter(e.target.value)} />
            <label style={styles.check}>
              <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
              Machine is DOWN / Maschine STEHT
            </label>
            <button style={{ ...styles.btn, opacity: busy || !text.trim() ? 0.5 : 1 }}
              disabled={busy || !text.trim()} onClick={submit}>
              {busy ? '…' : 'Send report / Meldung senden'}
            </button>
            {error && <p style={styles.err}>{error}</p>}
            {errorDetail && <p style={styles.errDetail}>{errorDetail}</p>}
          </>
        )}
      </div>
    </div>
  );
}
